import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import FanLevelBadge from "@/components/fan/FanLevelBadge";
import {   PillChip, TabBar, SegmentedControl, InfoCard, PrimaryButton   } from "@/components/toggles";
import { ArrowLeft, Users, Flame, Sparkles, UserPlus, Send, X, ShieldCheck } from "lucide-react";

const TABS = [
  { key: null, label: "Alle" },
  { key: "TOP_FAN", label: "Top-Fans", Icon: Flame },
  { key: "ACTIVE_FAN", label: "Aktive Fans", Icon: Sparkles },
  { key: "NEW_FAN", label: "Neue Fans", Icon: UserPlus },
];

// iter107 (2026-02): Area-Scope-Tabs mit STX-Anonymisierung
// iter107d (Msg 807): Neuer "Social"-Tab (reine Follower ohne Käufe)
const AREA_TABS = [
  { key: null, label: "Alle Bereiche" },
  { key: "SOCIAL", label: "Social" },
  { key: "MP", label: "Marketplace" },
  { key: "STX", label: "stilltouchedX" },
];

export default function StudioFansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fans, setFans] = useState([]);
  const [stats, setStats] = useState({ TOP_FAN: 0, ACTIVE_FAN: 0, NEW_FAN: 0, total: 0 });
  const [level, setLevel] = useState(null);
  const [area, setArea] = useState(null); // null | "MP" | "STX"
  const [loading, setLoading] = useState(true);
  const [showBroadcast, setShowBroadcast] = useState(false);

  const load = async (filterLvl, filterArea) => {
    setLoading(true);
    try {
      const qStats = filterArea ? `?area=${filterArea}` : "";
      const qFans = new URLSearchParams();
      if (filterLvl) qFans.set("level", filterLvl);
      if (filterArea) qFans.set("area", filterArea);
      const [{ data: st }, { data: fs }] = await Promise.all([
        api.get(`/seller/fans/stats${qStats}`),
        api.get(`/seller/fans${qFans.toString() ? `?${qFans}` : ""}`),
      ]);
      setStats(st);
      setFans(fs.fans || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(level, area); }, [level, area]);

  return (
    <div data-testid="studio-fans-page" className="min-h-full bg-white text-[#0A0A0A] pb-16">
      {/* Header */}
      <div className="sticky top-0 z-40 flex items-center gap-3 px-4 py-2.5 bg-white border-b border-[#E5E5E5]">
        <button onClick={() => navigate(-1)} data-testid="studio-fans-back"
          className="w-9 h-9 -ml-1.5 flex items-center justify-center rounded-full hover:bg-[#F5F5F5]">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold leading-tight">{t("studio_fans.page_title")}</h1>
          <p className="text-[10px] text-[#888] mt-0.5">{t("studio_fans.title")}</p>
        </div>
        <button data-testid="fans-broadcast-open"
          onClick={() => setShowBroadcast(true)}
          className="h-8 px-3 rounded-full bg-[#0A0A0A] text-white text-[11px] font-semibold flex items-center gap-1.5 hover:bg-[#333] transition-colors">
          <Send size={12} /> Broadcast
        </button>
      </div>

      {showBroadcast && <BroadcastModal stats={stats} onClose={() => setShowBroadcast(false)} />}

      {/* iter107: Area-Scope-Tabs (Alle · MP · STX). STX-Tab → anonymisierte Ansicht. */}
      <TabBar
        testId="fans-area-tabs"
        tabs={AREA_TABS.map((a) => ({
          key: a.key || "all",
          label: a.label,
          testId: `fans-area-${a.key ? a.key.toLowerCase() : "all"}`,
        }))}
        activeKey={area || "all"}
        onChange={(k) => setArea(k === "all" ? null : k)}
        className="px-4 pt-3"
      />

      {/* STX-Anonymitäts-Hinweis */}
      {area === "STX" && (
        <div data-testid="fans-stx-anonymity-notice"
          className="mx-4 mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <ShieldCheck size={14} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-[11px] leading-relaxed text-amber-900">
            <strong>Anonymisierte Ansicht.</strong> Käufer im stilltouchedX-Bereich werden nur mit ihrem STX-Alias angezeigt — kein Klarname, kein Avatar, keine Verlinkung.
          </div>
        </div>
      )}

      {/* Stat-Cards */}
      <div className="grid grid-cols-3 gap-2 px-4 pt-4">
        <div className="rounded-xl bg-[#F5F5F5] border border-[#E5E5E5] p-3 text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Flame size={13} className="text-[#0A0A0A]" />
            <p className="text-xl font-bold tabular-nums" data-testid="fans-stat-top">{stats.TOP_FAN}</p>
          </div>
          <p className="text-[10px] text-[#666] uppercase tracking-wider">Top</p>
        </div>
        <div className="rounded-xl bg-[#F5F5F5] border border-[#E5E5E5] p-3 text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Sparkles size={13} className="text-[#0A0A0A]" />
            <p className="text-xl font-bold tabular-nums" data-testid="fans-stat-active">{stats.ACTIVE_FAN}</p>
          </div>
          <p className="text-[10px] text-[#666] uppercase tracking-wider">Aktiv</p>
        </div>
        <div className="rounded-xl bg-[#F5F5F5] border border-[#E5E5E5] p-3 text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <UserPlus size={13} className="text-[#0A0A0A]" />
            <p className="text-xl font-bold tabular-nums" data-testid="fans-stat-new">{stats.NEW_FAN}</p>
          </div>
          <p className="text-[10px] text-[#666] uppercase tracking-wider">Neu</p>
        </div>
      </div>

      {/* Filter-Tabs */}
      <div className="flex gap-2 px-4 pt-4 pb-3 overflow-x-auto scrollbar-hide">
        {TABS.map((tt) => (
          <PillChip
            key={tt.key || "all"}
            testId={`fans-tab-${tt.key ? tt.key.toLowerCase() : "all"}`}
            active={level === tt.key}
            onClick={() => setLevel(tt.key)}
            variant="neutral"
            size="md"
          >
            {tt.label}
          </PillChip>
        ))}
      </div>

      {/* Fan-Liste */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      ) : fans.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-[#F5F5F5] mx-auto flex items-center justify-center mb-3">
            <Users size={22} className="text-[#888]" />
          </div>
          <p className="text-sm text-[#666]">Noch keine {level ? "Fans in dieser Kohorte" : "Fans"}.</p>
          <p className="text-[10px] text-[#999] mt-1">{t("studio_fans.empty")}</p>
        </div>
      ) : (
        <ul data-testid="fans-list" className="divide-y divide-[#F0F0F0]">
          {fans.map((f) => {
            const isAnon = f.is_anonymous;
            const initialChar = (f.display_name || "?")[0]?.toUpperCase() || "?";
            return (
              <li key={f.buyer_id} className="flex items-center gap-3 px-4 py-3">
                <button
                  onClick={() => !isAnon && f.handle && navigate(`/user/${f.handle}/social`)}
                  disabled={isAnon || !f.handle}
                  data-testid={`fan-avatar-${f.buyer_id}`}
                  className={`w-11 h-11 rounded-full overflow-hidden shrink-0 border border-[#E5E5E5] ${
                    isAnon ? "bg-[#0A0A0A] cursor-default" : "bg-[#F5F5F5] disabled:opacity-40"
                  }`}
                >
                  {isAnon ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShieldCheck size={16} className="text-amber-400" />
                    </div>
                  ) : f.avatar_url ? (
                    <img src={f.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-sm font-bold text-[#888]">
                      {initialChar}
                    </div>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                    <p className={`text-sm font-semibold truncate ${isAnon ? "font-mono text-[#0A0A0A]" : ""}`}
                       data-testid={`fan-name-${f.buyer_id}`}>
                      {f.display_name || "—"}
                    </p>
                    <FanLevelBadge level={f.level} area={area === "STX" ? "STX" : "MP"} showLabel={false} />
                    {isAnon && (
                      <span data-testid={`fan-anonymous-badge-${f.buyer_id}`}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                        Anon
                      </span>
                    )}
                    {f.subscription_tier && (
                      <span data-testid={`fan-sub-tier-${f.buyer_id}`}
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider border ${
                          f.subscription_tier === "VIP" ? "bg-amber-50 text-amber-700 border-amber-200" :
                          f.subscription_tier === "PREMIUM" ? "bg-purple-50 text-purple-700 border-purple-200" :
                          "bg-blue-50 text-blue-700 border-blue-200"
                        }`}
                      >{f.subscription_tier}-Abo</span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#888] mt-0.5 tabular-nums">
                    {f.purchases > 0 && <>{f.purchases} Käufe · </>}
                    {f.total_spend_eur > 0 && <>LTV {f.total_spend_eur.toFixed(2)} € · </>}
                    folgt seit {f.days_since_follow !== null ? `${f.days_since_follow}d` : "?"}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------
   Broadcast-Modal · Segment-Filter via Fan-Level (iter88.16)
   ------------------------------------------------------------- */
function BroadcastModal({ stats, onClose }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [levels, setLevels] = useState([]); // [] = alle
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const toggleLevel = (lvl) => {
    setLevels((prev) => (prev.includes(lvl) ? prev.filter((l) => l !== lvl) : [...prev, lvl]));
  };

  const send = async () => {
    if (title.trim().length < 2 || body.trim().length < 2) {
      setError(t("studio_fans.title_msg_required"));
      return;
    }
    setSending(true);
    setError("");
    try {
      const payload = { title: title.trim(), body: body.trim() };
      if (levels.length > 0) payload.fan_levels = levels;
      const { data } = await api.post("/seller/broadcasts", payload);
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Broadcast konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  const cohortLabel = { TOP_FAN: "Top-Fans", ACTIVE_FAN: "Aktive Fans", NEW_FAN: "Neue Fans" };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         data-testid="broadcast-modal">
      <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-sm font-bold">{t("studio_fans.send_broadcast")}</h2>
          <button data-testid="broadcast-close" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F5F5]">
            <X size={16} />
          </button>
        </div>

        {result ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
              <Send size={22} className="text-green-600" />
            </div>
            <p className="text-sm font-semibold text-[#0A0A0A]">Gesendet an {result.recipient_count} Empfänger</p>
            <p className="text-[11px] text-[#666] mt-1">
              {(result.fan_levels || []).length > 0
                ? `Nur ${(result.fan_levels || []).map((l) => cohortLabel[l]).join(", ")}`
                : "Alle Follower & Watchlist-User"}
            </p>
            <PrimaryButton variant="primary" size="sm" testId="broadcast-done" onClick={onClose}>{t("common.close")}</PrimaryButton>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#666] block mb-1">Titel</label>
              <input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)}
                maxLength={100} placeholder="Kurzer Betreff"
                className="w-full h-10 px-3 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0A0A0A]" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#666] block mb-1">{t("studio_fans.message")}</label>
              <textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)}
                maxLength={1000} rows={4} placeholder={t("studio_fans.msg_placeholder")}
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm resize-none focus:outline-none focus:border-[#0A0A0A]" />
              <p className="text-[10px] text-[#999] text-right mt-0.5 tabular-nums">{body.length}/1000</p>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#666] block mb-2">Segment</label>
              <div className="flex flex-wrap gap-2">
                <PillChip
                  testId="broadcast-segment-all"
                  active={levels.length === 0}
                  onClick={() => setLevels([])}
                  variant="neutral"
                  size="md"
                >
                  Alle ({stats.total})
                </PillChip>
                {["TOP_FAN", "ACTIVE_FAN", "NEW_FAN"].map((lvl) => (
                  <PillChip
                    key={lvl}
                    testId={`broadcast-segment-${lvl.toLowerCase()}`}
                    active={levels.includes(lvl)}
                    onClick={() => toggleLevel(lvl)}
                    variant="neutral"
                    size="md"
                  >
                    {cohortLabel[lvl]} ({stats[lvl] || 0})
                  </PillChip>
                ))}
              </div>
              <p className="text-[10px] text-[#999] mt-2">{t("studio_fans.no_segment")}</p>
            </div>
            {error && (
              <InfoCard variant="danger">
                {error}
              </InfoCard>
            )}
            <button data-testid="broadcast-send" onClick={send} disabled={sending || title.length < 2 || body.length < 2}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-[#333] transition-colors disabled:opacity-40">
              <Send size={14} /> {sending ? "Wird gesendet..." : "Broadcast senden"}
            </button>
            <p className="text-[10px] text-[#999] text-center">Max. 1 Broadcast pro Stunde.</p>
          </div>
        )}
      </div>
    </div>
  );
}
